import React from "react";
import * as R from "ramda";

import Table from "@arachnys/styleguide/Table";
import { DEFAULT_PAGESIZE } from "../config";
import { debounce } from "../util";

// `ApolloPaginationTable` is a container providing logic for handling basic
// pagination, and filtering for GraphQL w/ Relay queries.
class ApolloPaginationTable extends React.Component {
    defaultPageSize = this.props.defaultPageSize || DEFAULT_PAGESIZE;
    state = {
        page: 0,
        pageSize: this.defaultPageSize,
    };
    loadedPages = [this.state.page];
    paginationCalled = false;

    // `getVisibleData` returns data to be shown on the table based on the
    // current page, and desired number of results.
    // Ideally, the parent should provide the exact data to display.
    // Our use of Relay however, is built fundamentally on cursor-pagination,
    // and we will be fetching more data rather than a "window" of data.
    // We should probably not have a UI that is built around offset-limit pagination.
    getVisibleData = (data, page, pageSize) => {
        if (!data) {
            return [];
        }
        const offset = page * pageSize;
        return data.slice(offset, offset + pageSize);
    };

    _fireOnFilteredChange = debounce((refetch, filters) => {
        if (this.props.onFilteredChange) {
            this.addPageToLoaded(0);
            this.props.onFilteredChange(refetch, filters);
        }
    }, 500);

    // `getConnectionFromResponse` extracts the Relay connection
    // data from an arbitrary payload.
    // It does so by assuming that it is the first item in an object.
    // e.g. { searchEntities: { ... } }
    // In the example above, `searchEntities` will contain data corresponding
    // to a Relay model. i.e. Contains `edges`, and `pageInfo` at the minimum.
    // TODO: rely on `__typename` or some other identifier in the future.
    getConnectionFromResponse = R.pipe(R.values, R.head);

    getEdgesFromConnection = R.propOr([], "edges");

    getPageInfoFromConnection = R.propOr({}, "pageInfo");

    // `getConnectionFieldName` is similar to `getConnectionFromResponse`
    // but we are getting the name of the response field that contains
    // the Relay data.
    getConnectionFieldName = R.pipe(R.keys, R.head);

    // after pressing pagination buttons handleFetchData calls twice
    // so we cache page number to prevent double items loading
    // if handleFetchData calls not because of pagination but because of filtering or sorting
    // data is changed so table opens 1st page and removes pages cache
    addPageToLoaded = page => {
        if (this.paginationCalled && !this.loadedPages.includes(page)) {
            this.loadedPages.push(page);
        } else {
            this.loadedPages = [0];
        }
    };

    handleFilteredChange = refetch => filters =>
        this._fireOnFilteredChange(refetch, filters);

    // `shouldLoadMore` determines if we need to fetch more data
    // based on whether we changed the desired "number of results",
    // or if we simply need more results because we the page we are on
    // has not yet been fetched.
    // TODO: we should probably tie this with the fetcher more so the
    // fetcher knows exactly how many results it should be fetching.
    shouldLoadMore = (edges, variables, state) => {
        const differentPageSize = variables.count !== state.pageSize;
        const needMoreResults =
            edges.length < (state.page + 1) * state.pageSize &&
            state.totalCount > state.pageSize;
        const isPageLoaded =
            this.loadedPages.includes(state.page) && this.paginationCalled;

        return !isPageLoaded && (differentPageSize || needMoreResults);
    };

    // `handleFetchData` is fired when React Table paginates, sorts or filters.
    // It is used for refetching, and paginating using Apollo GraphQL (w/ Relay convention).
    // It takes care of constructing the final results based on the new data it
    // has received from the API.
    handleFetchData = ({ data, loading, variables, fetchMore }) => state => {
        if (loading) {
            return;
        }

        const connection = this.getConnectionFromResponse(data);
        const edges = this.getEdgesFromConnection(connection);

        if (edges.length === 0) {
            return;
        }
        this.addPageToLoaded(state.page);

        // `onFetchData` is being used for loading more data.
        // As such, we do not care about any other variables apart from changes
        // to the end cursor or page size.
        if (this.shouldLoadMore(edges, variables, state)) {
            const { endCursor } = this.getPageInfoFromConnection(connection);

            fetchMore({
                variables: {
                    ...variables,
                    cursor: endCursor,
                    count: state.pageSize,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const fieldName = this.getConnectionFieldName(
                        fetchMoreResult
                    );
                    const connection = this.getConnectionFromResponse(
                        fetchMoreResult
                    );
                    const newEdges = this.getEdgesFromConnection(connection);
                    const newPageInfo = this.getPageInfoFromConnection(
                        connection
                    );

                    // NOTE: probably simpler with https://github.com/mweststrate/immer
                    return newEdges.length
                        ? {
                              [fieldName]: {
                                  ...previousResult[fieldName],
                                  edges: [
                                      ...previousResult[fieldName].edges,
                                      ...newEdges,
                                  ],
                                  pageInfo: {
                                      ...previousResult[fieldName].pageInfo,
                                      ...newPageInfo,
                                  },
                              },
                          }
                        : previousResult;
                },
            });
        }
    };

    // `handlePageChange` manages the React Table page state.
    // We need to control the paging instead of letting React Table
    // handle it because we want to be able to reset the page when
    // filtering has been carried out.
    // e.g. Jump to page 3, change search filters, reset page back to 1.
    // Otherwise we may run into issues with data not loading or the current page
    // being greater than the total number of pages available.
    handlePageChange = page => {
        this.paginationCalled = true;
        this.setState(
            {
                page,
            },
            () => {
                this.paginationCalled = false;
            }
        );
    };

    componentDidUpdate = prevProps => {
        // We should reset the page if the filters have changed.
        // It will reduce the likelihood of problems arising,
        // e.g. avoid cases where we are trying to access a page that is out of bounds.
        const shouldResetPage = !R.equals(
            prevProps.apolloProps.variables,
            this.props.apolloProps.variables
        );

        if (shouldResetPage) {
            this.setState({
                page: 0,
            });
        }
    };

    render() {
        const { data, loading, refetch } = this.props.apolloProps;

        const connection = this.getConnectionFromResponse(data);
        const edges = this.getEdgesFromConnection(connection);
        const { totalCount } = this.getPageInfoFromConnection(connection);

        const props = {
            ...this.props,
            // `manual` tells React Table that we are handling pagination, filtering,
            // and sorting ourselves (server-side).
            manual: true,
            data: this.getVisibleData(
                edges,
                this.state.page,
                this.state.pageSize
            ),
            totalCount,
            defaultPageSize: this.defaultPageSize,
            loading,
            minRows: 5,
            page: this.state.page,
            pages: Math.ceil(totalCount / this.state.pageSize),
            onFilteredChange: this.handleFilteredChange(refetch),
            onFetchData: this.handleFetchData(this.props.apolloProps),
            onPageChange: this.handlePageChange,
        };

        return <Table {...props} />;
    }
}

export default ApolloPaginationTable;
